<template>
    <section class="avail-week">
        <div class="avail-week__grid">
            <div v-for="col in weekColumns" :key="col.dateStr" :class="columnClasses(col)">
                <header :class="headerClasses(col)">
                    <span :class="dayNameClasses(col)">{{ col.dayName }}</span>
                    <span :class="dayNumClasses(col)">{{ col.dayNum }}</span>
                </header>

                <div class="avail-week__slots-area">
                    <div
                        v-for="(slot, idx) in col.slots"
                        :key="idx"
                        v-clickaway="closeAllContextMenus"
                        :class="slotChipClasses(slot)"
                        :title="slotTooltip(slot)"
                        @click="onChipClick($event, slot)"
                    >
                        <svg
                            v-if="selectMode && isSlotSelected(slot)"
                            class="avail-week__chip-check"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <circle cx="10" cy="10" r="10" fill="#3b82f6" />
                            <path
                                d="M6 10.5l3 3 5-5"
                                stroke="#fff"
                                stroke-width="1.8"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                        </svg>
                        <div class="avail-week__chip-main">
                            <span class="avail-week__chip-time">{{ slot.startTime }}</span>
                            <button
                                v-if="!selectMode && (slot.referral || slot.isUnavailable)"
                                type="button"
                                class="avail-week__chip-dots"
                                @click.stop="toggleContextMenu(col.dateStr, idx)"
                            >
                                ···
                            </button>
                        </div>
                        <span v-if="slot.referral && slot.referral.patientName" class="avail-week__chip-label">
                            {{ slot.referral.patientName }}
                        </span>
                        <span v-else-if="slot.isUnavailable" class="avail-week__chip-label">
                            {{ $t('pages.availability.index.calendar.blocked') }}
                        </span>
                        <referral-context-menu
                            v-if="!selectMode && openMenu === col.dateStr + '-' + idx"
                            :optionProp="makeOption(slot)"
                            :showCancelled="showCancelled"
                            type="availability"
                            @closeReferralContextMenu="openMenu = null"
                            @schedule-changed="emitEvent({ name: 'schedule-changed' })"
                        ></referral-context-menu>
                    </div>
                </div>

                <div class="avail-week__col-count">
                    <span :class="slotCountClasses(col)">{{ col.totalSlots }} slot{{ col.totalSlots !== 1 ? 's' : '' }}</span>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
    import dayjs from 'dayjs'
    import weekday from 'dayjs/plugin/weekday'
    import { mapGetters, mapMutations } from 'vuex'
    import ReferralStatusEnum from '../../../enums/ReferralStatusEnum'
    import ReferralContextMenu from '../ReferralContextMenu'

    dayjs.extend(weekday)

    const CONFIRMED_STATUSES = [
        ReferralStatusEnum.PATIENT_VISIT_PENDING,
        ReferralStatusEnum.INSURANCE_AUTH_PENDING,
        ReferralStatusEnum.CONSULTATION_NOTES_PENDING,
        ReferralStatusEnum.TIME_SLOTS_PROPOSED,
        ReferralStatusEnum.COMPLETED,
        ReferralStatusEnum.SCHEDULED,
    ]

    const PENDING_STATUSES = [
        ReferralStatusEnum.CREATED,
        ReferralStatusEnum.RESCHEDULE_REQUESTED,
        ReferralStatusEnum.RESCHEDULE_REQUESTED_BY_PATIENT,
        ReferralStatusEnum.RESCHEDULE_AVAILABILITY_CHANGE,
    ]

    const CANCELLED_STATUSES = [ReferralStatusEnum.CANCELLED, ReferralStatusEnum.NO_SHOW]

    export default {
        components: {
            ReferralContextMenu,
        },

        props: {
            year: { type: Number, required: true },
            month: { type: Number, required: true },
            dates: { type: Object, default: () => ({}) },
            physician: { type: Object, default: () => ({}) },
            practice: { type: Object, default: () => ({}) },
            showCancelled: { type: Boolean, default: false },
        },

        inject: {
            isProposeFlow: {
                default: false,
            },
        },

        data() {
            return {
                openMenu: null,
            }
        },

        computed: {
            ...mapGetters('availability', ['selectMode', 'selectedSlots']),
            currentDate() {
                return this.$route.query.date ? dayjs(this.$route.query.date) : dayjs().year(this.year).month(this.month).date(1)
            },

            weekStart() {
                return this.currentDate.startOf('week')
            },

            weekColumns() {
                return Array.from({ length: 7 }, (_, i) => {
                    const d = this.weekStart.add(i, 'day')
                    const dateStr = d.startOf('day').format()
                    let slots = this.dates[dateStr] || []

                    if (d.isBefore(dayjs(), 'date')) {
                        slots = slots.filter((s) => s.referral)
                    }

                    if (!this.showCancelled) {
                        slots = slots.filter((s) => !s.referral || !CANCELLED_STATUSES.includes(s.referral.status))
                    }

                    return {
                        dayName: d.format('ddd'),
                        dayNum: d.date(),
                        dateStr,
                        date: d,
                        isToday: d.isSame(dayjs(), 'date'),
                        slots,
                        totalSlots: slots.length,
                    }
                })
            },
        },

        methods: {
            ...mapMutations('availability', ['TOGGLE_SLOT']),
            isSlotSelected(slot) {
                const key = `${slot.date}|${slot.startTime}|${slot.endTime}`
                return this.selectedSlots.some((s) => `${s.date}|${s.startTime}|${s.endTime}` === key)
            },
            toggleSlotSelection(slot) {
                if (slot.referral) return
                if (this.isProposeFlow && slot.isUnavailable) return
                this.TOGGLE_SLOT(slot)
            },
            columnClasses(col) {
                return {
                    'avail-week__col': true,
                    'avail-week__col--today': col.isToday,
                    'avail-week__col--empty': col.slots.length === 0,
                }
            },

            headerClasses(col) {
                return {
                    'avail-week__col-header': true,
                    'avail-week__col-header--today': col.isToday,
                }
            },

            dayNameClasses(col) {
                return {
                    'avail-week__day-name': true,
                    'avail-week__day-name--today': col.isToday,
                }
            },

            slotCountClasses(col) {
                return {
                    'avail-week__slot-count': true,
                    'avail-week__slot-count--has-slots': col.totalSlots > 0,
                }
            },

            dayNumClasses(col) {
                return {
                    'avail-week__day-num': true,
                    'avail-week__day-num--today': col.isToday,
                }
            },

            slotChipClasses(slot) {
                return {
                    'avail-week__chip': true,
                    'avail-week__chip--confirmed': slot.referral && CONFIRMED_STATUSES.includes(slot.referral.status),
                    'avail-week__chip--pending': slot.referral && PENDING_STATUSES.includes(slot.referral.status),
                    'avail-week__chip--cancelled': slot.referral && CANCELLED_STATUSES.includes(slot.referral.status),
                    'avail-week__chip--unavailable': slot.isUnavailable && !slot.referral,
                    'avail-week__chip--available': !slot.referral && !slot.isUnavailable,
                    'avail-week__chip--selected': this.selectMode && this.isSlotSelected(slot),
                    'avail-week__chip--selectable': this.selectMode && !slot.referral,
                }
            },

            chipStatusLabel(slot) {
                if (!slot.referral) return ''
                if (CONFIRMED_STATUSES.includes(slot.referral.status)) return 'Confirmed'
                if (PENDING_STATUSES.includes(slot.referral.status)) return 'Pending'
                if (CANCELLED_STATUSES.includes(slot.referral.status)) return 'Cancelled'
                return ''
            },

            slotTooltip(slot) {
                const time = `${slot.startTime} - ${slot.endTime}`
                if (slot.referral) return `${time} (${this.chipStatusLabel(slot)})`
                if (slot.isUnavailable) return `${time} (Unavailable)`
                return `${time} (Available)`
            },

            onChipClick(event, slot) {
                if (this.selectMode) {
                    event.stopPropagation()
                    this.toggleSlotSelection(slot)
                    return
                }
                // Outside select mode, prevent the column-level click from navigating
                // when the user is interacting with a chip (the `...` button handles its
                // own click; this stops a stray click on the chip body from drilling in).
                event.stopPropagation()
            },

            toggleContextMenu(dateStr, idx) {
                const key = dateStr + '-' + idx
                this.openMenu = this.openMenu === key ? null : key
            },

            closeContextMenu(dateStr, idx) {
                const key = dateStr + '-' + idx
                if (this.openMenu === key) {
                    this.openMenu = null
                }
            },

            closeAllContextMenus() {
                this.openMenu = null
            },

            makeOption(slot) {
                return {
                    label: [slot.startTime, slot.endTime].join(' - '),
                    value: slot,
                }
            },
            navigateToDay(col) {
                this.$router
                    .push({
                        name: this.$route.name,
                        params: this.$route.params,
                        query: {
                            ...this.$route.query,
                            view: 'daily',
                            date: col.date.format('YYYY-MM-DD'),
                        },
                    })
                    .catch(() => {})
            },
        },
    }
</script>

<style scoped>
    .avail-week {
        width: 100%;
        padding: 24px 24px 0;
        position: relative;
        box-sizing: border-box;
    }

    .avail-week__grid {
        display: grid;
        grid-template-columns: repeat(7, minmax(0, 1fr));
        gap: 12px;
        width: 100%;
    }

    .avail-week__col {
        display: flex;
        flex-direction: column;
        background: #ffffff;
        border: 1px solid #e5e7eb;
        border-radius: 16px;
        overflow: hidden;
        box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
    }

    .avail-week__col--today {
        border-color: #a5f3fc;
    }

    .avail-week__col--empty {
        background: #ffffff;
    }

    .avail-week__col-header {
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 12px 12px 10px;
        background: #f9fafb;
        border-bottom: 1px solid #e5e7eb;
        border-radius: 16px 16px 0 0;
        cursor: default;
    }

    .avail-week__col-header--today {
        background: #ecfeff;
        border-bottom-color: #cffafe;
    }

    .avail-week__col-count {
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 10px 12px;
        background: #f9fafb;
        border-top: 1px solid #e5e7eb;
        border-radius: 0 0 16px 16px;
        cursor: default;
    }

    .avail-week__day-name {
        font-size: 11px;
        font-weight: 400;
        color: #9ca3af;
        text-transform: uppercase;
        letter-spacing: 0.04em;
    }

    .avail-week__day-name--today {
        color: #06b6d4;
    }

    .avail-week__day-num {
        font-size: 20px;
        font-weight: 500;
        color: #1f2937;
        line-height: 1;
        margin-top: 2px;
    }

    .avail-week__day-num--today {
        color: #06b6d4;
    }

    .avail-week__slot-count {
        font-size: 12px;
        color: #9ca3af;
        font-weight: 500;
    }

    .avail-week__slot-count--has-slots {
        color: #3b82f6;
    }

    .avail-week__slots-area {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 6px;
        padding: 12px 10px;
        overflow-y: auto;
        background: #ffffff;
    }

    .avail-week__chip {
        display: flex;
        flex-direction: column;
        gap: 2px;
        padding: 8px 10px;
        border-radius: 10px;
        font-size: 12px;
        line-height: 1.3;
        transition: opacity 0.1s;
    }

    .avail-week__chip:hover {
        opacity: 0.85;
    }

    .avail-week__chip--confirmed {
        background: #22c55e;
        color: #ffffff;
    }

    .avail-week__chip--pending {
        background: #f97316;
        color: #ffffff;
    }

    .avail-week__chip--cancelled {
        background: #fde8e8;
        border: 1.5px dashed #e05252;
        color: #e05252;
    }

    .avail-week__chip--unavailable {
        background: #fde8e8;
        border: 1.5px dashed #e05252;
        color: #e05252;
    }

    .avail-week__chip--available {
        background: #f3f4f6;
        border: 1px solid #e5e7eb;
        color: #374151;
    }

    .avail-week__chip-main {
        display: flex;
        align-items: center;
        gap: 4px;
    }

    .avail-week__chip-time {
        font-weight: 400;
        white-space: nowrap;
    }

    .avail-week__chip-dots {
        font-size: 14px;
        font-weight: 700;
        line-height: 1;
        letter-spacing: 1px;
        background: transparent;
        border: 0;
        padding: 0 4px;
        cursor: pointer;
        color: inherit;
        margin-left: auto;
    }

    .avail-week__chip-dots:hover {
        opacity: 0.7;
    }

    .avail-week__chip {
        position: relative;
    }

    .avail-week__chip ::v-deep .dropdown-menus.referral-actions {
        position: absolute;
        top: calc(100% + 4px);
        right: 0;
        z-index: 50;
        background: #ffffff;
        border: 1px solid #e5e7eb;
        border-radius: 12px;
        box-shadow: 0 10px 25px rgba(0, 0, 0, 0.12);
        min-width: 220px;
        padding: 8px;
        text-align: left;
        color: #111827;
        font-weight: 500;
    }

    .avail-week__chip ::v-deep .dropdown-menus.referral-actions .menu-item {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 8px 10px;
        cursor: pointer;
        border-radius: 8px;
        font-size: 14px;
        transition: background 0.15s;
    }

    .avail-week__chip ::v-deep .dropdown-menus.referral-actions .menu-item:hover {
        background: #f9fafb;
    }

    .avail-week__chip ::v-deep .dropdown-menus.referral-actions .menu-item.disabled-item {
        opacity: 0.5;
        cursor: not-allowed;
    }

    .avail-week__chip-label {
        font-size: 11px;
        font-weight: 500;
        opacity: 0.9;
    }

    .avail-week__chip-check {
        width: 16px;
        height: 16px;
        flex-shrink: 0;
        margin-bottom: 2px;
    }

    .avail-week__chip--selectable {
        cursor: pointer;
    }

    .avail-week__chip--selected {
        border: 2px solid #3b82f6 !important;
        background: #eff6ff !important;
        color: #1d4ed8 !important;
    }

    @media (max-width: 1024px) {
        .avail-week__grid {
            gap: 8px;
        }
        .avail-week__col {
            border-radius: 12px;
        }
        .avail-week__col-header {
            border-radius: 12px 12px 0 0;
            padding: 10px 8px 8px;
        }
        .avail-week__col-count {
            border-radius: 0 0 12px 12px;
        }
        .avail-week__day-num {
            font-size: 20px;
        }
        .avail-week__chip {
            padding: 6px 8px;
            font-size: 11px;
        }
    }

    @media (max-width: 768px) {
        .avail-week__grid {
            grid-template-columns: repeat(7, 1fr);
            gap: 4px;
        }
        .avail-week__col {
            border-radius: 10px;
        }
        .avail-week__col-header {
            border-radius: 10px 10px 0 0;
            padding: 8px 4px 6px;
        }
        .avail-week__col-count {
            border-radius: 0 0 10px 10px;
        }
        .avail-week__day-name {
            font-size: 10px;
        }
        .avail-week__day-num {
            font-size: 16px;
        }
        .avail-week__chip-time {
            font-size: 9px;
        }
        .avail-week__chip-label {
            display: none;
        }
        .avail-week__slot-count {
            font-size: 10px;
        }
    }

    @media (max-width: 425px) {
        .avail-week__grid {
            grid-template-columns: repeat(3, 1fr);
        }
    }
</style>
